import type { FC, ReactNode, SVGProps } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { useKnockClient, useNotificationStore, useNotifications } from '@knocklabs/react';
import { Box, Button, CircularProgress, Divider, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { CheckIcon, colors, DocumentIcon, NoResults, SettingsIcon, ToolsIcon, UsersBoldIcon } from '@truvhq/ui';
import DOMPurify from 'dompurify';
import { useHistory } from 'react-router-dom';

import { formatNotificationDate } from 'shared/lib/datetime/formatNotificationDate';
import { urls } from 'shared/urls';

import { KNOCK_FEED_CHANNEL_ID, type NotificationCategory } from '../lib/constants';
import { getCategoryFromSource, getRenderedContent, getWorkflowCategories } from '../lib/helpers';
import MarkAllAsReadIcon from './icons/mark-all-as-read.svg?react';
import MarkAsUnreadIcon from './icons/mark-as-unread.svg?react';

type FeedClient = ReturnType<typeof useNotifications>;

type NotificationsFeedProps = {
  bellFeedClient: FeedClient;
  onClose: () => void;
};

const getCategoryIcon = (category: NotificationCategory): ReactNode => {
  const iconProps: SVGProps<SVGSVGElement> = { width: 20, height: 20, className: 'shrink-0' };

  switch (category) {
    case 'team':
      return <UsersBoldIcon {...iconProps} />;
    case 'api':
      return <ToolsIcon {...iconProps} />;
    case 'orders':
    case 'all':
      return <DocumentIcon {...iconProps} />;
  }
};

export const NotificationsFeed: FC<NotificationsFeedProps> = ({ bellFeedClient, onClose }) => {
  const history = useHistory();
  const [activeTab, setActiveTab] = useState<NotificationCategory>('all');

  const knockClient = useKnockClient();

  const workflowCategories = useMemo(() => getWorkflowCategories(activeTab), [activeTab]);

  const feedClient = useNotifications(knockClient, KNOCK_FEED_CHANNEL_ID, {
    workflow_categories: workflowCategories,
  });

  const { items, metadata, loading, networkStatus } = useNotificationStore(feedClient);

  useEffect(() => {
    feedClient.fetch();
  }, [feedClient]);

  const handleMarkAllAsRead = () => {
    bellFeedClient.markAllAsRead();
    feedClient.markAllAsRead();
  };

  const handleOpenSettings = () => {
    history.push(urls.notifications.url());
    onClose();
  };

  return (
    <Box className="flex h-[min(600px,100dvh-80px)] flex-col">
      {/* Header */}
      <Box className="flex items-center justify-between px-4 pt-3 pb-2">
        <Typography variant="h3" className="text-base font-semibold">
          Notifications
        </Typography>
        <Box className="flex items-center gap-2">
          <Tooltip title="Settings" placement="top">
            <IconButton
              sx={{ '& path': { stroke: colors.grey60, strokeWidth: 2 } }}
              size="small"
              onClick={handleOpenSettings}
              aria-label="Open settings"
            >
              <SettingsIcon width={24} height={24} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Mark all as read" placement="top">
            <IconButton size="small" onClick={handleMarkAllAsRead} aria-label="Mark all as read">
              <MarkAllAsReadIcon className="text-grey60" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Tabs */}
      <Box className="border-b border-grey30">
        <Tabs
          value={activeTab}
          onChange={(_, value: NotificationCategory) => setActiveTab(value)}
          sx={{
            minHeight: 40,
            px: 2,
            '& .MuiTab-root': {
              minWidth: 0,
              '&.Mui-selected': {
                color: 'primary.main',
              },
            },
          }}
        >
          <Tab label="All" value="all" />
          <Tab label="Orders" value="orders" />
          <Tab label="Team" value="team" />
          <Tab label="API" value="api" />
        </Tabs>
      </Box>

      {/* Content */}
      <Box className="flex-1 overflow-y-auto">
        {loading && items.length === 0 ? (
          <Box className="flex items-center justify-center py-12">
            <CircularProgress size={24} />
          </Box>
        ) : items.length === 0 ? (
          <NoResults
            className="min-h-[400px] flex-1 max-w-[420px] "
            title="No notifications"
            description="Once something important happens, you'll see it here"
          />
        ) : (
          <Box>
            {items.map((item) => {
              const renderedContent = getRenderedContent(item.blocks);
              const category = getCategoryFromSource(item.source);
              const icon = getCategoryIcon(category);
              const isUnread = !item.read_at;

              return (
                <Box key={item.id} className="group flex items-start gap-3 px-4 py-3 transition-colors hover:bg-grey20">
                  <Box className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-grey20">{icon}</Box>

                  {/* Content */}
                  <Box className="min-w-0 flex-1">
                    {renderedContent && (
                      <Typography
                        className="text-sm leading-snug [&_a]:text-primary-main [&_a]:underline"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(renderedContent, {
                            ALLOWED_TAGS: ['p', 'a', 'strong', 'em', 'br'],
                            ALLOWED_ATTR: ['href', 'target', 'rel'],
                          }),
                        }}
                      />
                    )}
                    <Typography className="mt-1 text-xs text-grey60">
                      {formatNotificationDate(new Date(item.inserted_at))}
                    </Typography>
                  </Box>

                  {/* Hover action / Unread indicator */}
                  <Box className="flex w-6 shrink-0 items-center justify-center">
                    {isUnread ? (
                      <>
                        <Box className="mt-1.5 h-2 w-2 rounded-full bg-primary-main group-hover:hidden" />
                        <Tooltip title="Mark as read" placement="right">
                          <IconButton
                            size="small"
                            className="hidden group-hover:flex"
                            onClick={(e) => {
                              e.stopPropagation();
                              bellFeedClient.markAsRead(item);
                              feedClient.markAsRead(item);
                            }}
                            aria-label="Mark as read"
                          >
                            <CheckIcon width={24} height={24} className="text-grey60" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <Tooltip title="Mark as unread" placement="right">
                        <IconButton
                          size="small"
                          className="hidden group-hover:flex"
                          onClick={(e) => {
                            e.stopPropagation();
                            bellFeedClient.markAsUnread(item);
                            feedClient.markAsUnread(item);
                          }}
                          aria-label="Mark as unread"
                        >
                          <MarkAsUnreadIcon className="text-grey60" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Load more */}
        {metadata.total_count > items.length && (
          <>
            <Divider className="my-3 mx-4" />
            <Button
              variant="text"
              disabled={networkStatus === 'fetchMore'}
              onClick={() => feedClient.fetchNextPage()}
              className="ml-3 mb-5"
            >
              {networkStatus === 'fetchMore' ? <CircularProgress size={16} className="my-1" /> : 'See more'}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};
